import { useMemo, useState } from "react";
import { api, fmtEUR } from "@/lib/api";

/**
 * CastingPrefillBanner · C8 — pre-rellena el Cap.02 (Personal artístico)
 * desde los personajes ya detectados en Casting.
 *
 * Solo se renderiza si el proyecto tiene `casting.characters` con elementos.
 *
 * Mapeo IA-importance → código ICAA:
 *   protagonista → 02.01     · 12.000€ por persona
 *   secundario   → 02.03     ·  6.000€
 *   episódico    → 02.04     ·    800€
 *   figurante    → 02.05     ·    200€
 *
 * Por defecto SUMA cantidades a las líneas existentes (no destructivo). El
 * usuario puede activar "Reemplazar" para sobrescribir las líneas 02.01-02.05.
 */
const TARIFAS_DEFAULT = { "02.01": 12000, "02.03": 6000, "02.04": 800, "02.05": 200 };
const IMPORTANCE_LABEL = {
  protagonista: { code: "02.01", label: "Protagonistas" },
  secundario: { code: "02.03", label: "Secundarios" },
  episódico: { code: "02.04", label: "Reparto / pequeñas" },
  episodico: { code: "02.04", label: "Reparto / pequeñas" },
  figurante: { code: "02.05", label: "Figuración" },
};

export default function CastingPrefillBanner({ project, projectId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [force, setForce] = useState(false);
  const [tarifas, setTarifas] = useState(TARIFAS_DEFAULT);

  const buckets = useMemo(() => {
    const out = { "02.01": [], "02.03": [], "02.04": [], "02.05": [] };
    for (const c of (project.casting?.characters || [])) {
      const meta = IMPORTANCE_LABEL[(c.importance || "").toLowerCase()];
      if (meta) out[meta.code].push(c.name || "Sin nombre");
    }
    return out;
  }, [project]);

  const totalPersonajes = Object.values(buckets).reduce((s, arr) => s + arr.length, 0);
  const totalImporte = Object.entries(buckets).reduce(
    (s, [code, arr]) => s + arr.length * (tarifas[code] || 0),
    0,
  );

  if (totalPersonajes === 0) {
    return (
      <div className="bg-amber-50/60 px-4 py-2.5 text-[12px] text-amber-900 sm:px-6">
        ℹ Hay {project.casting.characters.length} personajes en Casting, pero ninguno tiene la <em>importancia</em> necesaria (protagonista / secundario / episódico / figurante). Edita los personajes en la tab <strong>Casting</strong>.
      </div>
    );
  }

  const apply = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/icaa/projects/${projectId}/budget/prefill-from-casting`, { force, tarifas });
      setOpen(false);
      await onApplied?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error al prellenar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-l-[3px] border-l-gb-red bg-gb-paper-soft/50 px-4 py-3 sm:px-6" data-testid="casting-prefill-banner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-red">
            ✦ Prellenar desde Casting
          </p>
          <p className="mt-0.5 text-sm text-gb-black/80">
            Hay <strong>{totalPersonajes}</strong> personaje{totalPersonajes === 1 ? "" : "s"} en Casting con importancia reconocida.
            Estimación: <strong className="font-mono tabular-nums">{fmtEUR(totalImporte)}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(true)} data-testid="casting-prefill-btn"
                  className="rounded-full bg-gb-red px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white transition hover-gb-red">
            Prellenar
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-gb-black/70 p-4 backdrop-blur"
             onClick={() => !busy && setOpen(false)} data-testid="casting-prefill-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-7 shadow-2xl">
            <p className="gb-eyebrow">Cap.02 · Personal artístico</p>
            <h2 className="mt-1 gb-display text-2xl">Prellenar desde Casting</h2>
            <p className="mt-2 text-sm text-gb-muted">
              Cada personaje detectado por la IA en el guion se mapea a una línea del presupuesto según su importancia. Edita las tarifas antes de aplicar.
            </p>

            <div className="mt-5 space-y-3">
              {Object.entries(buckets).map(([code, names]) => (
                names.length > 0 && (
                  <div key={code} className="rounded-md border border-gb-paper bg-gb-surface p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gb-black">
                        <span className="font-mono text-gb-red">{code}</span>
                        {" · "}
                        {Object.values(IMPORTANCE_LABEL).find((x) => x.code === code)?.label}
                      </p>
                      <span className="text-[10px] uppercase tracking-widest text-gb-muted">
                        {names.length} × {fmtEUR(tarifas[code])} = {fmtEUR(names.length * tarifas[code])}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-[12px] italic text-gb-muted">
                      {names.join(" · ")}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-[11px]">
                      <span className="uppercase tracking-widest text-gb-muted">Tarifa €:</span>
                      <input type="number" min="0" step="100" value={tarifas[code]}
                             onChange={(e) => setTarifas({ ...tarifas, [code]: Number(e.target.value) || 0 })}
                             data-testid={`casting-prefill-tarifa-${code}`}
                             className="w-24 rounded border border-gb-paper bg-white px-2 py-1 text-right font-mono outline-none focus:border-gb-red" />
                    </div>
                  </div>
                )
              ))}
            </div>

            <label className="mt-5 flex items-start gap-2 rounded-md bg-gb-paper-soft p-3 text-sm">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                     data-testid="casting-prefill-force"
                     className="mt-1" />
              <span>
                <strong className="block">Reemplazar líneas existentes</strong>
                <span className="text-gb-muted">
                  Si está marcado, sobrescribe las líneas 02.01-02.05 con los valores propuestos. Por defecto SUMA cantidades a lo que ya hay.
                </span>
              </span>
            </label>

            <div className="mt-5 flex items-baseline justify-between text-[12px]">
              <span className="uppercase tracking-widest text-gb-muted">Total estimado</span>
              <span className="font-mono text-lg tabular-nums text-gb-black">{fmtEUR(totalImporte)}</span>
            </div>

            {error && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy}
                      className="rounded-full border border-gb-paper px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-muted hover:bg-gb-paper-soft">
                Cancelar
              </button>
              <button onClick={apply} disabled={busy}
                      data-testid="casting-prefill-confirm"
                      className="rounded-full bg-gb-red px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red disabled:opacity-40">
                {busy ? "Aplicando…" : force ? "Reemplazar y aplicar" : "Sumar y aplicar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
